/**
 * Assignment (ADR-63 Cut 3) - the third of the four independent relationships:
 * a tenant's (or an internal department's) RIGHT TO USE a thing that HipNumber
 * may still own. It is NOT ownership: an asset owned by HipNumber can be rented
 * to a tenant, and the rental is the assignment, not the ownership.
 *
 * The doc is firm on one trap (section 18): TENANT_OWNED is an assignment
 * relationship kept for human readability, and it must NEVER be the source of
 * truth for ownership. Ownership is `owner_type` + `owner_tenant_id` on the
 * asset; this type only says how the tenant came to be using the thing.
 */

export const ASSIGNMENT_TYPES = [
  'RENTAL',
  'TENANT_OWNED',
  'LEASE',
  'LEASE_TO_OWN',
  'LOANER',
  'DEMO',
  'INTERNAL_USE',
  'TEMPORARY_DEPLOYMENT',
  'CUSTOMER_SUPPLIED',
  'OTHER',
] as const;
export type AssignmentType = (typeof ASSIGNMENT_TYPES)[number];

export const ASSIGNMENT_TYPE_LABEL: Readonly<Record<AssignmentType, string>> = {
  RENTAL: 'Rental',
  TENANT_OWNED: 'Tenant owned',
  LEASE: 'Lease',
  LEASE_TO_OWN: 'Lease to own',
  LOANER: 'Loaner',
  DEMO: 'Demo',
  INTERNAL_USE: 'Internal use',
  TEMPORARY_DEPLOYMENT: 'Temporary deployment',
  CUSTOMER_SUPPLIED: 'Customer supplied',
  OTHER: 'Other',
};

export const ASSIGNMENT_STATUSES = ['ACTIVE', 'CLOSED'] as const;
export type AssignmentStatus = (typeof ASSIGNMENT_STATUSES)[number];

export const BILLING_MODES = ['NONE', 'INCLUDED', 'MONTHLY_RENTAL', 'PER_AUCTION_RENTAL', 'ONE_TIME', 'LEASE', 'LEASE_TO_OWN', 'MANUAL'] as const;
export type BillingMode = (typeof BILLING_MODES)[number];

export const BILLING_MODE_LABEL: Readonly<Record<BillingMode, string>> = {
  NONE: 'No charge',
  INCLUDED: 'Included',
  MONTHLY_RENTAL: 'Monthly rental',
  PER_AUCTION_RENTAL: 'Per auction',
  ONE_TIME: 'One time',
  LEASE: 'Lease',
  LEASE_TO_OWN: 'Lease to own',
  MANUAL: 'Manual',
};

/**
 * The asset status an active assignment implies. The doc keeps status, owner
 * and assignment in separate columns (section 7); this is how the assignment
 * workflow SETS the status column, and the operator status control never offers
 * these directly (Cut 1's `ASSET_ASSIGNMENT_STATUSES`).
 */
export function assignmentAssetStatus(type: AssignmentType): 'ASSIGNED' | 'DEPLOYED' | 'LOANED' | 'RENTED' | 'LEASED' {
  switch (type) {
    case 'RENTAL': return 'RENTED';
    case 'LOANER': return 'LOANED';
    case 'LEASE': case 'LEASE_TO_OWN': return 'LEASED';
    case 'DEMO': case 'TEMPORARY_DEPLOYMENT': return 'DEPLOYED';
    default: return 'ASSIGNED';
  }
}

/** Return date cannot precede the start (doc validation rule 6). Pure, tested without a database. */
export function returnDateValid(startAt: string, actualReturnAt: string): boolean {
  const s = Date.parse(startAt); const r = Date.parse(actualReturnAt);
  return Number.isFinite(s) && Number.isFinite(r) && r >= s;
}

export function isAssignmentType(v: unknown): v is AssignmentType {
  return typeof v === 'string' && (ASSIGNMENT_TYPES as readonly string[]).includes(v);
}
